import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  name: string;
  surname: string;
  email: string;
  role: string;
  language: string;
  orgId: string | null;
  orgRole: string | null;
  orgName: string | null;
  orgSlug: string | null;
  orgPlan: string | null;
  emailVerified: boolean;
  needsReconsent: boolean;
}

interface AuthStore {
  // `token` and `refreshToken` are no longer stored in JS — they live in
  // HttpOnly cookies set by the API. We keep `null` here for back-compat
  // with components that read `useAuthStore().token` to gate UI.
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  setAuth: (token: string | null, refreshToken: string | null, user: User) => void;
  logout: () => void;
  isSuperAdmin: () => boolean;
  isOrgOwner: () => boolean;
  isOrgAdmin: () => boolean;
}

/**
 * Auth store. Holds only the user profile in memory + persisted to
 * localStorage under `logixa-auth`. Tokens themselves are HttpOnly cookies
 * set by the API on /auth/login and /auth/refresh — JS cannot read them
 * (which is the point: XSS cannot exfiltrate them).
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      setAuth: (_token, _refreshToken, user) => {
        // Tokens are ignored intentionally — they came in as HttpOnly cookies.
        // We accept them in the signature so call sites built before this
        // migration still compile; nothing reads them.
        set({ token: null, refreshToken: null, user });
      },
      logout: () => {
        // Best-effort cleanup of any localStorage keys older builds wrote.
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem('logixa_token');
            localStorage.removeItem('logixa-auth');
          } catch {/* private mode */}
        }
        set({ token: null, refreshToken: null, user: null });
      },
      isSuperAdmin: () => get().user?.role === 'SUPER_ADMIN',
      isOrgOwner: () => get().user?.orgRole === 'OWNER',
      isOrgAdmin: () => ['OWNER', 'ADMIN'].includes(get().user?.orgRole || ''),
    }),
    {
      name: 'logixa-auth',
      // Persist only the user profile, never tokens.
      partialize: state => ({ user: state.user }),
      // Bump on schema changes — older clients with persisted token/refreshToken
      // would otherwise leak fields that our new partialize doesn't expect.
      version: 2,
      migrate: (persistedState: any, _fromVersion: number) => {
        // Strip legacy token/refreshToken from any pre-v2 cached state.
        if (persistedState && typeof persistedState === 'object') {
          const { user } = persistedState as any;
          return { user: user ?? null };
        }
        return { user: null };
      },
      // If hydration ever throws (corrupt JSON, schema mismatch), wipe and
      // fall back to a fresh state instead of crashing the dashboard.
      onRehydrateStorage: () => (state, error) => {
        if (error && typeof window !== 'undefined') {
          try { localStorage.removeItem('logixa-auth'); } catch {/* private mode */}
        }
      },
    },
  ),
);
